import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "../../api/client";
import { JOBS } from "../../constants/testIds";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["all", "open", "active", "on_hold", "complete", "invoiced", "legacy"];

export default function JobsList() {
    const [jobs, setJobs] = useState([]);
    const [clients, setClients] = useState([]);
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ client_id: "", address: "", description: "", status: "open" });
    const nav = useNavigate();

    const load = async () => {
        const params = {};
        if (status !== "all") params.status = status;
        if (q) params.q = q;
        const { data } = await api.get("/jobs", { params });
        setJobs(data);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [status, q]);
    useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

    const create = async () => {
        setCreating(true);
        try {
            const { data } = await api.post("/jobs", { ...form, client_id: form.client_id || null });
            toast.success(`Job ${data.job_number} created`);
            setOpenDialog(false);
            setForm({ client_id: "", address: "", description: "", status: "open" });
            nav(`/admin/jobs/${data.id}`);
        } catch (e) {
            toast.error(apiErr(e));
        } finally {
            setCreating(false);
        }
    };

    return (
        <div data-testid={JOBS.root} className="space-y-8">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted mb-2">Admin</div>
                    <h1 className="font-extrabold tracking-[-0.03em] text-4xl leading-none">Jobs</h1>
                    <p className="text-muted mt-2 num">{jobs.length} shown</p>
                </div>
                <Button data-testid={JOBS.newBtn} onClick={() => setOpenDialog(true)} className="gap-2">
                    <Plus size={16} /> New job
                </Button>
            </div>

            <div className="km-card p-4 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px] relative">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                    <Input
                        data-testid={JOBS.search}
                        placeholder="Search number or address…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid={JOBS.filterStatus} className="w-[180px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="km-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left border-b border-[color:var(--km-border)] text-[11px] uppercase tracking-[0.12em] text-muted">
                            <th className="px-6 py-3 font-medium">Number</th>
                            <th className="px-6 py-3 font-medium">Address</th>
                            <th className="px-6 py-3 font-medium">Client</th>
                            <th className="px-6 py-3 font-medium">Status</th>
                            <th className="px-6 py-3 font-medium text-right">Quoted</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.map((j) => (
                            <tr
                                key={j.id}
                                data-testid={JOBS.row(j.id)}
                                className="border-b border-[color:var(--km-border)] hover:bg-[color:var(--surface-1)] cursor-pointer"
                                onClick={() => nav(`/admin/jobs/${j.id}`)}
                            >
                                <td className="px-6 py-4 num font-medium text-ink">{j.job_number || "—"}</td>
                                <td className="px-6 py-4 max-w-[380px]">
                                    <div className="text-ink truncate">{j.address}</div>
                                    <div className="text-xs text-muted truncate">{j.description}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-muted">{clientName(j.client_id)}</td>
                                <td className="px-6 py-4"><StatusBadge status={j.status} /></td>
                                <td className="px-6 py-4 num text-right text-ink">
                                    {j.quoted_amount != null ? `$${j.quoted_amount.toLocaleString()}` : "—"}
                                </td>
                                <td className="px-6 py-4 text-right text-xs text-muted">Open →</td>
                            </tr>
                        ))}
                        {!jobs.length && (
                            <tr><td colSpan={6} className="py-12 text-center text-muted text-sm">No jobs match.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-extrabold tracking-[-0.03em] text-2xl">New job</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Client</Label>
                            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                                <SelectTrigger><SelectValue placeholder="(no client)" /></SelectTrigger>
                                <SelectContent>
                                    {clients.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Address</Label>
                            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div>
                            <Label>Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {STATUSES.filter((s) => s !== "all").map((s) => (
                                        <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button onClick={create} disabled={creating || !form.address}>
                            {creating ? "Creating…" : "Create job"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
